/**
 * Permission Popup
 *
 * Shows a permission dialog for speech synthesis on platforms that
 * require user interaction (iOS, Android, Safari with fallback).
 */

import { gestureWouldHelp, type PlaybackCapabilities } from './capability-probe';
import { armGestureOnce } from './gesture';
import { renderListenPrompt, resolveListenLabel } from './listen-prompt-renderer';
import { generatePopupStyles, POPUP_STYLE_ID, POPUP_STYLES } from './popup-styles';

/**
 * Options for the permission popup (per-call overrides)
 */
export interface PermissionPopupOptions {
  /** Visible prompt label (default: "Tap to Listen") */
  label?: string;
  /** Accessible label and tooltip (default: "Tap to listen to voice playback") */
  tooltip?: string;
  /** Offset from the anchored edge in px (default: 20) */
  offset?: number;
  /** Offset from the anchored edge on screens ≤520px in px (default: 14) */
  mobileOffset?: number;
  /** Delay before the prompt appears, in ms; 0 shows it immediately (default: 1500) */
  gestureGraceMs?: number;
  /** Collapse to icon-only after this many ms; null keeps the full label (default: 7000) */
  collapseAfterMs?: number | null;
  /**
   * Override the origin/hostname shown in the popup.
   * @deprecated The prompt has no origin line; this is ignored.
   */
  urlOverride?: string;
  /**
   * Override the action text.
   * @deprecated The prompt has no action text; this is ignored.
   */
  textOverride?: string;
  /**
   * Override the ALLOW button label.
   * @deprecated Use {@link PermissionPopupOptions.label} instead.
   */
  allowLabel?: string;
  /**
   * Override the DENY button label.
   * @deprecated The prompt has no deny control; this is ignored.
   */
  denyLabel?: string;
  /** Position of the prompt (default: "bottom") */
  position?: 'top' | 'bottom';
  /** Append popup to a specific container instead of document.body */
  appendTo?: HTMLElement;
  /** CSS class prefix to replace the default "rv" prefix (e.g. "myapp" → "myappListenPrompt") */
  classPrefix?: string;
  /**
   * Custom renderer — return your own HTMLElement for full control.
   * Called with allow/deny callbacks; when invoked, skips the default popup entirely.
   */
  renderPopup?: (onAllow: () => void, onDeny: () => void) => HTMLElement;
}

/**
 * Configuration for PermissionPopup
 */
export interface PermissionPopupConfig {
  /** Callback when user allows or denies */
  onResponse?: (allowed: boolean) => void;
  /** Callback to trigger click event (iOS/Android unlock) */
  onClickEvent?: () => void;
}

/**
 * Scheduled speak data for resuming after permission
 */
export interface ScheduledSpeak {
  text: string;
  voiceName?: string;
  parameters?: Record<string, unknown>;
}

/**
 * Permission Popup Manager
 *
 * Handles the permission dialog for speech synthesis.
 */
export class PermissionPopup {
  private config: PermissionPopupConfig;
  private popupElement: HTMLElement | null = null;
  private styleElement: HTMLStyleElement | null = null;
  private appearanceCount = 0;
  private graceTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private collapseTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private fadingElement: HTMLElement | null = null;
  private disposeGesture: (() => void) | null = null;

  /** Maximum times to show the popup before giving up */
  private static readonly MAX_APPEARANCES = 2;

  /** Delay before the prompt appears, giving a natural gesture time to arrive */
  private static readonly DEFAULT_GRACE_MS = 1500;

  /** Delay before the visible prompt collapses to its icon */
  private static readonly DEFAULT_COLLAPSE_MS = 7000;

  /** Matches the stylesheet's opacity transition, so removal follows the fade */
  private static readonly FADE_OUT_MS = 200;

  /** User's permission response (null = not yet responded) */
  speechAllowedByUser: boolean | null = null;

  /** Disable the permission popup entirely */
  disablePopup = false;

  /** Force showing popup everywhere (not just mobile/Safari) */
  allowPopupEverywhere = false;

  /** Scheduled speak to execute after permission granted */
  scheduledSpeak: ScheduledSpeak | null = null;

  /** Default options applied to every showPopup call (user-configurable) */
  defaultOptions: PermissionPopupOptions = {};

  constructor(config: PermissionPopupConfig = {}) {
    this.config = config;
  }

  /**
   * Check if speech is allowed and show popup if needed
   *
   * @param options - Popup options
   * @param platformInfo - Platform detection info
   * @returns true if speech is allowed, false if popup was shown or denied
   */
  checkSpeechAllowed(
    options: PermissionPopupOptions = {},
    platformInfo: {
      isIOS: boolean;
      isAndroid: boolean;
      isSafari: boolean;
      isFallbackMode: boolean;
      isForcedFallback: boolean;
      clickEventDetected: boolean;
      speechSynthesisNotAllowedError?: boolean;
      /** Probe verdict; null while the probes are still running */
      capabilities?: PlaybackCapabilities | null;
      /** A user gesture is being handled right now */
      transientActivation?: boolean;
    }
  ): boolean {
    // If user explicitly denied, return false
    if (this.speechAllowedByUser === false) {
      return false;
    }

    // An explicit allow opens the gate for the session; without this the
    // sticky observed-block flag would re-block the very replay the user's
    // gesture just authorised.
    if (this.speechAllowedByUser === true) {
      return true;
    }

    // A speak carried by a live gesture is itself the permission grant; the
    // prompt exists only to obtain one. Dismissal happens on speech start.
    if (platformInfo.transientActivation === true) {
      return true;
    }

    // If popup is disabled, allow
    if (this.disablePopup) {
      return true;
    }

    // Nothing is blocked, so a gesture has nothing to unlock. Skip the prompt
    // even on platforms whose heuristics below would prompt.
    if (platformInfo.capabilities !== undefined && !gestureWouldHelp(platformInfo.capabilities)) {
      return true;
    }

    // Determine if we should show popup
    const onMobile = platformInfo.isAndroid || platformInfo.isIOS;
    const onSafariFallback =
      platformInfo.isSafari && (platformInfo.isFallbackMode || platformInfo.isForcedFallback);
    const observedBlock = platformInfo.speechSynthesisNotAllowedError === true;
    const shouldShowPopup =
      this.allowPopupEverywhere || onMobile || onSafariFallback || observedBlock;

    // A click is only evidence that speech would be permitted; an observed block
    // is proof it was not, so it overrides that assumption.
    if (shouldShowPopup && (observedBlock || !platformInfo.clickEventDetected)) {
      // Popup already showing, or already waiting out the grace window
      if (this.popupElement || this.graceTimer) {
        return false;
      }

      // The cap counts prompts the user actually saw. Grace windows cancelled
      // by a gesture and error-path re-entries reach this check without
      // rendering anything, so the counter moves in showPopup().
      if (this.appearanceCount >= PermissionPopup.MAX_APPEARANCES) {
        console.log('ResponsiveVoice: Speech not allowed by user');
        return false;
      }

      this.beginGestureGraceWindow(options);
      return false;
    }

    return true;
  }

  /**
   * Wait for a natural page gesture before prompting.
   *
   * A gesture inside the window retries speech and the prompt never appears;
   * otherwise it is shown when the window closes.
   */
  private beginGestureGraceWindow(options: PermissionPopupOptions = {}): void {
    const merged = { ...this.defaultOptions, ...options };
    const graceMs = merged.gestureGraceMs ?? PermissionPopup.DEFAULT_GRACE_MS;

    // During the grace window no prompt exists, so the gesture cannot be meant
    // for one; retry immediately.
    this.disposeGesture = armGestureOnce(() => {
      this.disposeGesture = null;
      this.handleResponse(true);
    });

    if (graceMs <= 0) {
      this.showPromptWithGestureRetry(options);
      return;
    }

    this.graceTimer = setTimeout(() => {
      this.graceTimer = null;
      this.showPromptWithGestureRetry(options);
    }, graceMs);
  }

  /**
   * Show the prompt and keep listening for page gestures.
   *
   * The retry is deferred a tick so the page's own handlers run first. A
   * handler that spoke clears {@link scheduledSpeak}, and the retry then finds
   * an empty queue and just dismisses the prompt.
   */
  private showPromptWithGestureRetry(options: PermissionPopupOptions): void {
    this.disposeGesture?.();
    this.showPopup(options);

    this.disposeGesture = armGestureOnce(() => {
      this.disposeGesture = null;
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null;
        if (!this.popupElement) return;
        this.handleResponse(true);
      }, 0);
    });
  }

  /**
   * Speech became audible, by any route.
   *
   * Dismisses the prompt and abandons a pending grace window; neither has
   * anything left to recover.
   */
  onSpeechStarted(): void {
    if (!this.popupElement && !this.graceTimer && !this.disposeGesture) return;
    this.speechAllowedByUser = true;
    this.hidePopup();
  }

  /** Cancel pending grace/retry timers and stop listening for gestures. */
  private clearGestureGraceWindow(): void {
    if (this.graceTimer) {
      clearTimeout(this.graceTimer);
      this.graceTimer = null;
    }
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.disposeGesture?.();
    this.disposeGesture = null;
  }

  /**
   * Show the permission popup
   */
  showPopup(options: PermissionPopupOptions = {}): void {
    // Check if we're in a browser environment
    if (typeof document === 'undefined') {
      return;
    }

    // Don't show if already showing
    if (this.popupElement) {
      return;
    }

    // A still-fading predecessor would briefly coexist with the new prompt.
    this.removeFadingElement();

    this.appearanceCount++;

    // Merge defaults with per-call options
    const merged = { ...this.defaultOptions, ...options };
    const prefix = merged.classPrefix ?? 'rv';
    const container = merged.appendTo ?? document.body;

    // Custom renderer — full user control
    if (merged.renderPopup) {
      this.popupElement = merged.renderPopup(
        () => this.handleResponse(true),
        () => this.handleResponse(false)
      );
      container.appendChild(this.popupElement);
      return;
    }

    this.injectStyles(prefix);

    this.popupElement = renderListenPrompt(
      {
        label: resolveListenLabel(merged),
        tooltip: merged.tooltip,
        prefix,
        position: merged.position,
        offset: merged.offset,
        mobileOffset: merged.mobileOffset,
      },
      () => this.handleResponse(true)
    );

    container.appendChild(this.popupElement);

    // Next frame, so the opacity transition has a start value to animate from.
    const element = this.popupElement;
    requestAnimationFrame(() => element.classList.add('is-visible'));

    // `??` would swallow the explicit null opt-out.
    const collapseAfterMs =
      merged.collapseAfterMs === undefined
        ? PermissionPopup.DEFAULT_COLLAPSE_MS
        : merged.collapseAfterMs;
    if (collapseAfterMs !== null && collapseAfterMs > 0) {
      this.collapseTimer = setTimeout(() => {
        this.collapseTimer = null;
        element.classList.add('is-collapsed');
      }, collapseAfterMs);
    }
  }

  /**
   * Handle user response (allow/deny)
   */
  handleResponse(allowed: boolean): void {
    this.hidePopup();
    this.speechAllowedByUser = allowed;

    if (allowed) {
      // Trigger click event for iOS/Android unlock
      this.config.onClickEvent?.();
    }

    // Notify callback
    this.config.onResponse?.(allowed);
  }

  /**
   * Hide and remove the popup
   */
  hidePopup(): void {
    this.clearGestureGraceWindow();

    if (this.collapseTimer) {
      clearTimeout(this.collapseTimer);
      this.collapseTimer = null;
    }

    const element = this.popupElement;
    if (!element) return;
    this.popupElement = null;
    this.fadingElement = element;

    // Only `is-visible` is removed: dropping `is-collapsed` here would snap the
    // full label back at full opacity for the fade's duration, since `display`
    // does not transition.
    element.classList.remove('is-visible');
    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      this.removeFadingElement();
    }, PermissionPopup.FADE_OUT_MS);
  }

  /** Detach the faded-out node; safe to call early to skip the fade. */
  private removeFadingElement(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    this.fadingElement?.parentNode?.removeChild(this.fadingElement);
    this.fadingElement = null;
  }

  /**
   * Inject CSS styles into the document
   */
  private injectStyles(prefix = 'rv'): void {
    if (typeof document === 'undefined') {
      return;
    }

    const styleId = prefix === 'rv' ? POPUP_STYLE_ID : `${prefix}-permission-popup-styles`;

    // Check if already injected
    if (document.getElementById(styleId)) {
      return;
    }

    this.styleElement = document.createElement('style');
    this.styleElement.id = styleId;
    this.styleElement.textContent = prefix === 'rv' ? POPUP_STYLES : generatePopupStyles(prefix);
    document.head.appendChild(this.styleElement);
  }

  /**
   * Remove injected styles
   */
  removeStyles(): void {
    if (this.styleElement?.parentNode) {
      this.styleElement.parentNode.removeChild(this.styleElement);
      this.styleElement = null;
    }
  }

  /**
   * Check if popup is currently showing
   */
  isShowing(): boolean {
    return this.popupElement !== null;
  }

  /**
   * Reset popup state (for testing)
   */
  reset(): void {
    this.hidePopup();
    this.removeFadingElement();
    this.removeStyles();
    this.speechAllowedByUser = null;
    this.appearanceCount = 0;
    this.scheduledSpeak = null;
  }

  /**
   * Dispose of all resources
   */
  dispose(): void {
    this.reset();
  }
}

/**
 * Create a new PermissionPopup instance
 */
export function createPermissionPopup(config?: PermissionPopupConfig): PermissionPopup {
  return new PermissionPopup(config);
}
